/**
 * Session cache for public landing semester payload (repeat visits / prefetch).
 */

import type { ActiveSemesterPayload } from '@services/publicLandingApi';

const CACHE_KEY = 'bhg-public-active-semester-v2';
const ABSENT_CACHE_KEY = 'bhg-public-active-semester-absent-v1';

export const PUBLIC_LANDING_SEMESTER_CACHE_MAX_AGE_MS = 10 * 60 * 1000;

/** How long to trust session "no active semester" without re-fetching. */
export const PUBLIC_LANDING_SEMESTER_ABSENT_MAX_AGE_MS = 5 * 60 * 1000;

interface CachedActiveSemester {
  data: ActiveSemesterPayload;
  fetchedAt: number;
}

export function readPublicLandingSemesterCache(): ActiveSemesterPayload | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedActiveSemester;
    if (!parsed?.data?.semesterId || typeof parsed.fetchedAt !== 'number') return null;
    if (Date.now() - parsed.fetchedAt > PUBLIC_LANDING_SEMESTER_CACHE_MAX_AGE_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function writePublicLandingSemesterCache(data: ActiveSemesterPayload): void {
  try {
    const payload: CachedActiveSemester = { data, fetchedAt: Date.now() };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode / quota — ignore
  }
}

export function clearPublicLandingSemesterCache(): void {
  try {
    sessionStorage.removeItem(CACHE_KEY);
  } catch {
    // ignore
  }
}

interface CachedAbsentSemester {
  fetchedAt: number;
}

export function readPublicLandingSemesterAbsentCache(): boolean {
  try {
    const raw = sessionStorage.getItem(ABSENT_CACHE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as CachedAbsentSemester;
    if (typeof parsed?.fetchedAt !== 'number') return false;
    if (Date.now() - parsed.fetchedAt > PUBLIC_LANDING_SEMESTER_ABSENT_MAX_AGE_MS) {
      sessionStorage.removeItem(ABSENT_CACHE_KEY);
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function writePublicLandingSemesterAbsentCache(): void {
  try {
    const payload: CachedAbsentSemester = { fetchedAt: Date.now() };
    sessionStorage.removeItem(CACHE_KEY);
    sessionStorage.setItem(ABSENT_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore
  }
}

export function clearPublicLandingSemesterAbsentCache(): void {
  try {
    sessionStorage.removeItem(ABSENT_CACHE_KEY);
  } catch {
    // ignore
  }
}
